// PRJ-01 (projector throw distance) — README.md.

import { mediaPictureSize } from '@roomtreatment/core';
import type { BuildContext } from '../context';
import type { Issue, RuleId } from '../types';
import { issueId } from '../copy';

const IN_TO_M = 0.0254;

/** min…max throw ratio, as published on a `projector` product (already
 *  unwrapped from its `Sourced<>` — callers pass `product.specs.throwRatio.v`). */
export interface ThrowRatio {
  min: number;
  max: number;
}

/** A metres range, its low end first. */
export interface RangeM {
  minM: number;
  maxM: number;
}

/** Image width from the screen's published diagonal and aspect, metres —
 *  throw ratio is a distance-to-IMAGE-width ratio, not the framed case.
 *  Exported for content pages that need the same number PRJ-01 checks against
 *  (a projector's screen-size fit range, gear-matching plan §4.1) — the
 *  formula does not change by being called from outside the rule. */
export function screenWidthM(diagonalIn: number, aspect: '16:9' | '2.35:1'): number {
  const [aw, ah] = aspect === '16:9' ? [16, 9] : [2.35, 1];
  return diagonalIn * (aw / Math.hypot(aw, ah)) * IN_TO_M;
}

/**
 * The throw-distance range a projector needs for a given image width — PRJ-01's
 * own `required range` line (`throwRatio.min…max × screen width`), pulled out
 * so a content page can ask "how far for THIS width" without a placed room.
 * `checkPrj01` below calls this rather than re-deriving it, so the rule and
 * this export can never drift apart.
 */
export function throwDistanceRangeM(throwRatio: ThrowRatio, widthM: number): RangeM {
  return { minM: throwRatio.min * widthM, maxM: throwRatio.max * widthM };
}

/**
 * The image-width range a projector can fill at a given (assumed) throw
 * distance — the exact inverse of `throwDistanceRangeM`: PRJ-01 asks "is this
 * throw distance within range for this width," this asks "which widths put
 * this throw distance within range." A smaller ratio fills a WIDER image at
 * the same distance, so the widest fit comes from `throwRatio.min` and the
 * narrowest from `throwRatio.max`.
 */
export function screenWidthRangeM(throwRatio: ThrowRatio, throwM: number): RangeM {
  return { minM: throwM / throwRatio.max, maxM: throwM / throwRatio.min };
}

/**
 * A projector model against the picture its setup throws: the room's own
 * screen size (a screen model sets it; a generic screen has one too), at the
 * distance the projector hangs from the screen's wall. Not applicable without
 * a projector model or while it throws at nothing.
 */
export function checkPrj01(ctx: BuildContext): { issues: Issue[]; unknown: { rule: RuleId; productIds: string[] }[] } {
  const { projector } = ctx;
  if (!projector || projector.throwM === null) return { issues: [], unknown: [] }; // not applicable

  const { throwRatio } = projector.specs;
  if (!throwRatio) return { issues: [], unknown: [{ rule: 'PRJ-01', productIds: [projector.product.id] }] };

  const { throwM } = projector;
  const widthM = mediaPictureSize('projector', projector.entry).w;
  const { minM, maxM } = throwDistanceRangeM(throwRatio.v, widthM);
  if (throwM >= minM && throwM <= maxM) return { issues: [], unknown: [] };

  const tooClose = throwM < minM;
  const subjects = [projector.entry.id];
  return {
    issues: [
      {
        id: issueId('PRJ-01', subjects),
        rule: 'PRJ-01',
        severity: 'error',
        text: `Projector too ${tooClose ? 'close' : 'far'} for this screen.`,
        fix: `Move it to ${minM.toFixed(1)}–${maxM.toFixed(1)} m from the screen.`,
        subjects,
      },
    ],
    unknown: [],
  };
}
